import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getBalanceUser } from "core";
import { toast } from "components/ui/toast";

// ===== Types =====
type BalanceContextType = {
    balance: number | null;
    isLoading: boolean;
    setBalance: (b: number) => void;
    refetchBalance: () => Promise<void>;
};

// ===== Context =====
const BalanceContext = createContext<BalanceContextType | null>(null);

// ===== Provider =====
export const BalanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [balance, setBalance] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Fetch balance manually
    const fetchBalance = useCallback(async () => {
        try {
            setIsLoading(true);
            const res = await getBalanceUser();
            const data = res?.data ?? res;
            if (typeof data.balance === "number") {
                setBalance(data.balance);
            } else {
                console.warn("⚠️ Unexpected balance response:", data);
                setBalance(null);
            }
        } catch (err) {
            console.error("❌ Failed to fetch user balance:", err);
            toast.error("Failed to fetch user balance");
            setBalance(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Load once on mount
    useEffect(() => {
        fetchBalance();
    }, [fetchBalance]);

    // Expose public methods
    const value: BalanceContextType = {
        balance,
        isLoading,
        setBalance,
        refetchBalance: fetchBalance,
    };

    return <BalanceContext.Provider value={value}>{children}</BalanceContext.Provider>;
};

// ===== Hook =====
export const useBalance = () => {
    const ctx = useContext(BalanceContext);
    if (!ctx) {
        throw new Error("useBalance must be used inside <BalanceProvider>");
    }
    return ctx;
};

// ===== Optional auto-registration config =====
export const ProviderConfig = { Component: BalanceProvider, priority: 3 };
